import { ReactComponent as CaretDownGlyph } from '@/assets/images/svg/glyphs/caret-down.svg';
import { ReactComponent as SearchGlyph } from '@/assets/images/svg/glyphs/search.svg';
import Heading from '@/components/_atoms/Heading';
import CustomerAwardWinnerCard from '@/components/_organisms/CustomerAwardWinnerCard';
import DropDownSelectors from '@/components/_organisms/DropDownSelectors';
import PrimaryInnerGlowButton from '@/components/buttons/primary';
import SimpleCTAFooter from '@/components/footers/SimpleCTAFooter';
import InputFieldElement from '@/components/forms/InputFieldElement';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { CustomerAwardsHomepageQuery } from '@/graphql';
import CustomerAwardsHomepageQueryDocument from '@/graphql/queries/CustomerAwardsHomepage.gql';
import { getSingleLink } from '@/lib/craft-utils';
import gtm from '@/lib/gtm';
import { getStaticPageProps } from '@/lib/next';
import { parseHtml } from '@/lib/parse-html';
import { getSingle, maybeNotSet, notEmpty } from '@/lib/typescript-helpers';
import ResourceCards from '@/modules/ResourceCards';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import { kebabCase } from 'lodash';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ArrayParam, StringParam, useQueryParam } from 'use-query-params';

interface CustomerAwardsHomepageProps extends CustomerAwardsHomepageQuery { }

const companySizes = ['11-100 employees', '101-1000 employees', '1001+ employees'];

const industries = [
  'Logistics',
  'Manufacturing',
  'Technology',
  'Travel & Hospitality',
  'Professional services',
  'Other',
];

type CategoryType = NonNullable<CustomerAwardsHomepageQuery['categories']>[number];
type WinnerType = NonNullable<CustomerAwardsHomepageQuery['entries']>[number];
type DataReturnType = (NonNullable<CategoryType> & {
  winners: NonNullable<WinnerType>[];
})[];

const CustomerAwardsHomepage: PageTemplate<CustomerAwardsHomepageProps> = ({ entry, entries, categories }) => {
  const [searchTerms, setSearchTerms] = useQueryParam('search', StringParam);
  const [selectedCompanySizes, setCompanySize] = useQueryParam('size', ArrayParam);
  const [selectedIndustries, setIndustry] = useQueryParam('industry', ArrayParam);
  const [showAll, setShowAll] = useState<boolean>(false);

  const heroCta = getSingle(entry?.heroCta);
  const heroImage = getSingle(entry?.heroImage);
  const footerCta = getSingle(entry?.moduleSimplectafooter);
  const footerPrimaryCta = getSingleLink(footerCta?.primaryCta);

  const data = useMemo<DataReturnType>(() => {
    return (categories ?? []).filter(notEmpty).map((category, index) => {
      if (!entries) return { ...category, winners: [] };

      let winners = entries.filter(notEmpty).filter((entry) => {
        const cat = getSingle(entry.customerAwardsCategories);

        return cat && cat.slug === category.slug;
      });

      if (index === 0) {
        if (searchTerms) {
          winners = winners.filter((winner) => {
            const search = searchTerms.toLowerCase().replaceAll(/\s/g, '');

            return winner.customerName!.toLowerCase().replaceAll(/\s/g, '').includes(search);
          });
        }

        if (selectedCompanySizes?.length) {
          winners = winners.filter((winner) => selectedCompanySizes.includes(winner.customerAwardsCompanySize!));
        }

        if (selectedIndustries?.length) {
          winners = winners.filter((winner) => selectedIndustries.includes(winner.customerAwardsIndustry!));
        }
      }

      return {
        ...category,
        winners,
      };
    });
  }, [categories, entries, searchTerms, selectedCompanySizes, selectedIndustries, showAll]);

  const resetFilters = () => {
    setSearchTerms(null);
    setCompanySize([]);
    setIndustry([]);
    setShowAll(false);
  };

  return (
    <PageWrapper>
      <GradientBackground>
        <StyledContainer>
          <Hero>
            <HeroLeft>
              <HeroEyebrow>{entry?.heroEyebrow}</HeroEyebrow>

              <HeroTitle>
                <Heading level={1}>{entry?.heroHeadline}</Heading>
              </HeroTitle>

              <HeroCopy>{parseHtml(entry?.heroParagraph)}</HeroCopy>

              <HeroCTA>
                <Link href={heroCta?.url!}>
                  <a>{heroCta?.text!} &rarr;</a>
                </Link>
              </HeroCTA>
            </HeroLeft>

            <HeroRight>
              {heroImage && <Image src={heroImage.url!} height={heroImage.height!} width={heroImage.width!} alt="" />}
            </HeroRight>
          </Hero>

          <AwardsOverview>
            <AwardsOverviewHeading>
              <Heading level={2}>{entry?.awardsCategorySectionHeadline ?? 'Awards Categories'}</Heading>
            </AwardsOverviewHeading>

            <AwardCategories>
              {data.map((category, i) => (
                <AwardCategory key={`awards-category-${category.awardCategory?.replace(/\s/g, '-') ?? i}`}>
                  <AwardTitle>
                    <Heading level={3}>{category.awardCategory}</Heading>
                  </AwardTitle>

                  <AwardDescription>
                    {category.shortDescription && parseHtml(category.shortDescription)}
                  </AwardDescription>

                  <AwardCTA>
                    <Link href={'#' + kebabCase(category.awardCategory!)} scroll={false}>
                      <a>See the winners &rarr;</a>
                    </Link>
                  </AwardCTA>
                </AwardCategory>
              ))}
            </AwardCategories>
          </AwardsOverview>
        </StyledContainer>
      </GradientBackground>

      <StyledContainer>
        {data.map((category, i) => (
          <CategorySection key={category.awardCategory}>
            <CategoryHeading id={kebabCase(category.awardCategory!)}>
              <CategoryLeft>
                <CategoryAwardVisual>
                  <Image src={category.badge[0]?.url!} alt="" height="200" width="200" />
                </CategoryAwardVisual>
              </CategoryLeft>

              <CategoryRight>
                <CategoryTitle>
                  <Heading level={2}>{category.awardCategory}</Heading>
                </CategoryTitle>

                <CategoryDescription>
                  {category.longDescription && parseHtml(category.longDescription)}
                </CategoryDescription>
              </CategoryRight>
            </CategoryHeading>

            {i === 0 && (
              <>
                <ResultsSummaryGrid>
                  <ResultsSummary>
                    {category.winners.length} result
                    {category.winners.length === 0 || category.winners.length > 1 ? 's' : ''} found
                  </ResultsSummary>

                  {searchTerms || (selectedCompanySizes ?? []).length > 0 || (selectedIndustries ?? []).length > 0 ? (
                    <ResultsReset>
                      <a onClick={resetFilters}>Show all {category.awardCategory} winners</a>
                    </ResultsReset>
                  ) : null}
                </ResultsSummaryGrid>

                <CategoryFilters>
                  <SearchInputWrapper>
                    <SearchInput
                      type="search"
                      placeholder="Search"
                      value={searchTerms ?? ''}
                      onInput={({ target }) => setSearchTerms((target as HTMLInputElement).value || null)}
                    />
                    <StyledSearchGlyph />
                  </SearchInputWrapper>

                  <DropDownSelectors
                    categories={[
                      {
                        title: 'Company Size',
                        filters: companySizes,
                        selections: selectedCompanySizes?.filter(notEmpty) ?? [],
                      },
                      {
                        title: 'Industry',
                        filters: industries,
                        selections: selectedIndustries?.filter(notEmpty) ?? [],
                      },
                    ]}
                    onFilterToggle={(category, filter, selected) => {
                      if (selected) {
                        switch (category.title) {
                          case 'Company Size':
                            setCompanySize([...(selectedCompanySizes ?? []), filter]);
                            break;
                          case 'Industry':
                            setIndustry([...(selectedIndustries ?? []), filter]);
                            break;
                        }
                      } else {
                        switch (category.title) {
                          case 'Company Size':
                            setCompanySize((selectedCompanySizes ?? []).filter((choice) => choice !== filter));
                            break;
                          case 'Industry':
                            setIndustry((selectedIndustries ?? []).filter((choice) => choice !== filter));
                            break;
                        }
                      }

                      gtm.track(selected ? 'checked_filter' : 'unchecked_filter', {
                        location: 'Game Changer Awards',
                        category_title: maybeNotSet(category.title),
                        filter_title: maybeNotSet(filter),
                      });
                    }}
                  />
                </CategoryFilters>
              </>
            )}

            <Winners>
              {category.winners.map((winner, index) => {
                const logo = getSingle(winner.customerLogo);
                const winnerCompanySize: string | null = winner.customerAwardsCompanySize ?? null;
                const winnerIndustry: string | null = winner.customerAwardsIndustry ?? null;

                if ((i === 0 && !showAll && index < 9) || i !== 0 || showAll) {
                  return (
                    <CustomerAwardWinnerCard
                      key={category.awardCategory + '-winner-' + index}
                      customerName={winner.customerName ?? 'Front Customer'}
                      customerLogoUrl={logo?.url!}
                      tags={[{ text: winnerCompanySize }, { text: winnerIndustry }]}
                      cta={{
                        text: 'View this win',
                        href: winner.winnerPageUrl!,
                      }}
                    />
                  );
                }
              })}
            </Winners>

            {i === 0 && !showAll && category.winners.length > 9 && (
              <LoadMore>
                <ClearFilters>
                  <a onClick={() => setShowAll(true)}>
                    Load more {category.awardCategory} winners <StyledCaretDownGlyph width={16} flip={false} />
                  </a>
                </ClearFilters>
              </LoadMore>
            )}

            {i === 0 && showAll && category.winners.length > 9 && (
              <LoadMore>
                <ClearFilters>
                  <a onClick={() => setShowAll(false)}>
                    Show fewer {category.awardCategory} winners <StyledCaretDownGlyph width={16} flip={true} />
                  </a>
                </ClearFilters>
              </LoadMore>
            )}

            {category.winners.length === 0 && (
              <NoResults>
                <div>0 results found</div>
                <ClearFilters>
                  <a onClick={resetFilters}>Show all {category.awardCategory} winners &rarr;</a>
                </ClearFilters>
              </NoResults>
            )}
          </CategorySection>
        ))}
      </StyledContainer>

      <ResourceCardsSection>
        <Container>
          <ResourceCards props={entry?.moduleResourceCards} />
        </Container>
      </ResourceCardsSection>

      {footerCta && footerPrimaryCta ? (
        <SimpleCTAFooter
          heading={footerCta.heading ?? 'Deliver exceptional service at scale'}
          ctas={
            <Link href={footerPrimaryCta.url}>
              <PrimaryInnerGlowButton
                onClick={() => {
                  gtm.track('click', {
                    category: `Footer Module | ${footerCta.heading ?? 'Deliver exceptional service at scale'}`,
                    element_type: 'button',
                    element_label: `Primary CTA | ${maybeNotSet(footerPrimaryCta.text)}`,
                  });
                }}
              >
                {footerPrimaryCta.text ?? ''}
              </PrimaryInnerGlowButton>
            </Link>
          }
          background={footerCta.background ?? undefined}
        />
      ) : null}
    </PageWrapper>
  );
};

// #region - Components

const PageWrapper = styled.div``;

const GradientBackground = styled.div`
  background-image: url('/assets/pages/customer-awards/GCA24_LP_gradientBG_320_2x.png');
  background-repeat: no-repeat;
  background-position-y: 0;
  background-size: 100% 100%;

  ${mediaBreakpointUp('tablet')} {
    background-image: url('/assets/pages/customer-awards/GCA24_LP_gradientBG_640_2x.png');
  }

  ${mediaBreakpointUp('desktop')} {
    background-image: url('/assets/pages/customer-awards/GCA24_LP_gradientBG_1024_2x.png');
  }

  ${mediaBreakpointUp('desktopWide')} {
    background-image: url('/assets/pages/customer-awards/GCA24_LP_gradientBG_1440_2x.png');
  }
`;

const StyledContainer = styled(Container)`
  display: grid;
  row-gap: ${remCalc(40)};

  ${mediaBreakpointUp('tablet')} {
  }

  ${mediaBreakpointUp('desktop')} {
    row-gap: ${remCalc(80)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    row-gap: ${remCalc(120)};
  }
`;

const Hero = styled.div`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT + 10)};

  ${mediaBreakpointUp('desktop')} {
    display: grid;
    grid-template-rows: auto;
    row-gap: 0;
    grid-template-columns: repeat(16, 1fr);
    column-gap: ${remCalc(16)};
    padding-top: ${remCalc(PRIMARY_NAV_HEIGHT + 40)};
  }
`;

const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  row-gap: ${remCalc(30)};
  margin-bottom: ${remCalc(30)};
  text-align: center;

  ${mediaBreakpointUp('desktop')} {
    grid-column: 1 / span 7;
    align-items: flex-start;
    text-align: left;
    margin-bottom: 0;
  }
`;

const HeroEyebrow = styled.div`
  font-weight: ${FONT_WEIGHTS.BOLD};
  text-transform: uppercase;
  letter-spacing: 1.28px;
`;

const HeroTitle = styled.div`
  h1 {
    ${customTextSize(32, 38)};

    ${mediaBreakpointUp('desktopWide')} {
      ${customTextSize(49, 58)};
    }
  }
`;

const HeroCopy = styled.div`
  p {
    margin-bottom: ${remCalc(20)};
  }

  p:last-of-type {
    margin-bottom: 0;
  }

  ul {
    list-style-position: inside;
    list-style-type: disc;
    margin-left: ${remCalc(24)};
  }

  ul li {
    margin: ${remCalc(5)};
  }
`;

const HeroCTA = styled.div`
  a {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

const HeroRight = styled.div`
  ${mediaBreakpointUp('desktop')} {
    grid-column: 9 / -1;
  }
`;

const AwardsOverview = styled.div`
  ${mediaBreakpointUp('desktop')} {
    ${paddingX(60)};
  }
`;

const AwardsOverviewHeading = styled.div`
  text-align: center;
  margin-bottom: ${remCalc(30)};

  ${mediaBreakpointUp('tablet')} {
    h2 {
      ${customTextSize(32, 38)};
    }
  }

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: ${remCalc(60)};
  }

  ${mediaBreakpointUp('desktop')} {
    h2 {
      ${customTextSize(49, 58)};
    }
  }
`;

const AwardCategories = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto;
  row-gap: ${remCalc(80)};

  > div:last-child {
    margin-bottom: ${remCalc(20)};
  }

  ${mediaBreakpointUp('tablet')} {
    column-gap: ${remCalc(20)};
    grid-template-columns: 1fr 1fr;

    > div:last-child {
      margin-bottom: ${remCalc(60)};
    }
  }

  ${mediaBreakpointUp('desktop')} {
    column-gap: ${remCalc(130)};
    grid-template-columns: 1fr 1fr;

    > div:last-child {
      margin-bottom: ${remCalc(80)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    column-gap: ${remCalc(172)};
    grid-template-columns: 1fr 1fr;

    > div:last-child {
      margin-bottom: ${remCalc(120)};
    }
  }
`;

const AwardCategory = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(20)};
  margin: 0 auto;
  align-items: center;
  text-align: center;
`;

const AwardTitle = styled.div`
  h3 {
    ${customTextSize(32, 38)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

const AwardDescription = styled.div`
  ${customTextSize(14, 20)};
`;

const AwardCTA = styled.div`
  a {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

const CategorySection = styled.div`
  display: grid;
  row-gap: ${remCalc(30)};

  ${mediaBreakpointUp('desktop')} {
    row-gap: ${remCalc(60)};
  }
`;

const CategoryHeading = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(20)};

  ${mediaBreakpointUp('desktop')} {
    display: grid;
    grid-template-rows: auto;
    row-gap: 0;
    grid-template-columns: repeat(12, 1fr);
    column-gap: ${remCalc(20)};
  }
`;

const CategoryLeft = styled.div`
  ${mediaBreakpointUp('desktop')} {
    grid-column: 2 / span 3;
    margin: 0 auto;
  }
`;

const CategoryAwardVisual = styled.div`
  img {
    margin: 0 auto;
    height: ${remCalc(166)};
    width: ${remCalc(166)};

    ${mediaBreakpointUp('tablet')} {
      height: ${remCalc(157)};
      width: ${remCalc(157)};
    }

    ${mediaBreakpointUp('desktop')} {
      height: ${remCalc(198)};
      width: ${remCalc(198)};
    }

    ${mediaBreakpointUp('desktopWide')} {
      height: ${remCalc(285)};
      width: ${remCalc(285)};
    }
  }
`;

const CategoryRight = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(20)};

  ${mediaBreakpointUp('desktop')} {
    grid-column: 5 / -1;
    row-gap: ${remCalc(30)};
  }
`;

const CategoryTitle = styled.div``;

const CategoryDescription = styled.div`
  ${customTextSize(14, 20)};

  p {
    margin-bottom: ${remCalc(20)};
  }

  p:last-of-type {
    margin-bottom: 0;
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(16, 24)};

    p {
      margin-bottom: ${remCalc(30)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(24, 34)};
  }
`;

const ResultsSummaryGrid = styled.div`
  display: flex;
  gap: ${remCalc(20)};
  flex-direction: column;

  ${mediaBreakpointUp('tablet')} {
    flex-direction: row;
  }
`;

const ResultsSummary = styled.div`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
`;

const ResultsReset = styled.div`
  a {
    cursor: pointer;
  }
`;

const CategoryFilters = styled.div`
  border-top: 1px solid ${COLORS.GRAY.HIGHLIGHT};
  padding-top: ${remCalc(30)};
  display: flex;
  flex-direction: column;
  gap: ${remCalc(20)};

  ${mediaBreakpointUp('tablet')} {
    display: grid;
    grid-template-columns: repeat(12, 1fr);

    > div:first-child {
      grid-column: 1 / span 12;
    }

    > div:nth-child(2) {
      grid-column: 1 / span 12;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    > div:first-child {
      grid-column: 1 / span 4;
    }

    > div:nth-child(2) {
      grid-column: 5 / span 8;
    }
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  border: 1px solid ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(50)};
  width: 100%;
`;

const SearchInput = styled(InputFieldElement)`
  --input-border-radius: ${remCalc(50)};
  appearance: none;
  background-color: transparent;
  padding-left: ${remCalc(40)};

  &::-ms-clear,
  &::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }
  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
  }

  &:hover,
  &:active,
  &:focus {
    border-color: ${COLORS.BLACK} !important;
    border-radius: ${remCalc(50)};
  }
`;

const StyledSearchGlyph = styled(SearchGlyph)`
  position: absolute;
  top: ${remCalc(10)};
  left: ${remCalc(10)};
  width: ${remCalc(20)};
  height: ${remCalc(20)};

  path {
    fill: ${hexToRGBA(COLORS.BLACK, 0.4)};
  }
`;

const StyledCaretDownGlyph = styled(CaretDownGlyph) <{ flip: boolean }>`
  display: inline-block;
  rotate: ${(props) => (props.flip ? '180deg' : '0deg')};
  margin-left: ${remCalc(8)};
`;

const ClearFilters = styled.div`
  a {
    cursor: pointer;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

const LoadMore = styled.div`
  margin: 0 auto;
  text-align: center;
`;

const Winners = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${remCalc(20)};

  ${mediaBreakpointUp('desktop')} {
    display: grid;
    grid-template-columns: repeat(2, 50%);
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: repeat(3, ${remCalc(386)});
  }
`;

const NoResults = styled.div`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin: 0 auto;
  text-align: center;

  div:first-child {
    ${customTextSize(32, 38)};
    margin-bottom: ${remCalc(30)};
  }
`;

const ResourceCardsSection = styled.section`
  background: var(--Gradients-Dark, linear-gradient(209deg, ${COLORS.BLACK} 23.25%, ${COLORS.BLUE.LOWLIGHT} 161.08%));
  margin-top: ${remCalc(40)};
  margin-bottom: ${remCalc(40)};
  ${paddingY(60)};

  h3 {
    color: ${COLORS.WHITE};
  }

  ${mediaBreakpointUp('tablet')} {
    margin-top: ${remCalc(60)};
    margin-bottom: ${remCalc(60)};
  }

  ${mediaBreakpointUp('desktop')} {
    margin-top: ${remCalc(80)};
    margin-bottom: ${remCalc(80)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-top: ${remCalc(120)};
    margin-bottom: ${remCalc(120)};
  }
`;

// #endregion - Components

CustomerAwardsHomepage.template = MasterTemplate;

export default CustomerAwardsHomepage;

export const getStaticProps = getStaticPageProps('game-changer-awards', async ({ client }) => {
  const props = await client.request<CustomerAwardsHomepageQuery>(CustomerAwardsHomepageQueryDocument);

  if (!props.entry) {
    return { notFound: true };
  }

  return { props };
});
